'use client';

import { createListCollection, Flex, Input, SimpleGrid, Switch, Text } from '@chakra-ui/react';
import { memo } from 'react';

import SelectField from '@features/spend/SelectField';

const currencies = createListCollection({
  items: [
    { value: 'GHS', label: 'GHS' },
    { value: 'NGN', label: 'NGN' },
    { value: 'KES', label: 'KES' },
    { value: 'USD', label: 'USD' },
  ],
});

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
  fontSize: 'sm',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Flex direction="column" gap={1}>
    <Text fontSize="xs" color="text.secondary">
      {label}
    </Text>
    {children}
  </Flex>
);

interface Props {
  title: string;
  onTitleChange: (value: string) => void;
  clientName: string;
  onClientNameChange: (value: string) => void;
  targetAmount: string;
  onTargetAmountChange: (value: string) => void;
  currency: string;
  onCurrencyChange: (value: string) => void;
  allowPartial: boolean;
  onAllowPartialChange: (value: boolean) => void;
}

// The shared "collect from client" inputs — used both in the simplified payment
// builder and inside the Payment Link node's config on the routing canvas.
const CollectFromClientForm = memo(
  ({
    title,
    onTitleChange,
    clientName,
    onClientNameChange,
    targetAmount,
    onTargetAmountChange,
    currency,
    onCurrencyChange,
    allowPartial,
    onAllowPartialChange,
  }: Props) => {
    const onTitle = (e: React.ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value);
    const onClient = (e: React.ChangeEvent<HTMLInputElement>) => onClientNameChange(e.target.value);
    const onTarget = (e: React.ChangeEvent<HTMLInputElement>) => onTargetAmountChange(e.target.value);
    const onCurrency = (details: { value: string[] }) => onCurrencyChange(details.value[0] ?? 'GHS');
    const onPartial = (details: { checked: boolean }) => onAllowPartialChange(details.checked);

    return (
      <Flex direction="column" gap={4}>
        <Field label="Title">
          <Input placeholder="e.g. Invoice 4421 — Customer X" value={title} onChange={onTitle} {...inputProps} />
        </Field>
        <Field label="Client name">
          <Input placeholder="Who is paying" value={clientName} onChange={onClient} {...inputProps} />
        </Field>
        <SimpleGrid columns={2} gap={4}>
          <Field label="Target amount">
            <Input placeholder="0.00" type="number" value={targetAmount} onChange={onTarget} {...inputProps} />
          </Field>
          <Field label="Client currency">
            <SelectField
              collection={currencies}
              placeholder="GHS"
              value={[currency]}
              onValueChange={onCurrency}
            />
          </Field>
        </SimpleGrid>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          bg="surface.muted"
          borderRadius="md"
          px={4}
          py={3}
        >
          <Flex direction="column">
            <Text fontSize="sm" fontWeight="medium">
              Allow partial payments
            </Text>
            <Text fontSize="xs" color="text.secondary">
              The client can pay in bits until the target is reached.
            </Text>
          </Flex>
          <Switch.Root checked={allowPartial} onCheckedChange={onPartial} size="sm">
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
        </Flex>
      </Flex>
    );
  },
);
CollectFromClientForm.displayName = 'CollectFromClientForm';

export default CollectFromClientForm;
